// src/hooks/clientes/use-filial-form.ts
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { ClienteFilialDTO } from "@/types/cliente-filial.type";
import { useFilialActions } from "./use-filial-actions";

export const filialValidationSchema = z.object({
  nome: z.string().min(3, "O nome deve ter no mínimo 3 caracteres."),
  cnpj: z.string().optional(),
  grupo: z.string().optional(),
  clienteId: z.string().optional(), // Pode ser opcional se a filial ainda for "órfã"
});

export type FilialFormValues = z.infer<typeof filialValidationSchema>;

interface UseFilialFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filialToEdit?: ClienteFilialDTO | null;
}

export function useFilialForm({ open, onOpenChange, filialToEdit }: UseFilialFormProps) {
  const { updateFilial, isUpdating } = useFilialActions();

  const form = useForm<FilialFormValues>({
    resolver: zodResolver(filialValidationSchema),
    mode: "onChange",
    defaultValues: {
      nome: "",
      cnpj: "",
      grupo: "",
      clienteId: "",
    },
  });

  useEffect(() => {
    if (open && filialToEdit) {
      form.reset({
        nome: filialToEdit.nome,
        cnpj: filialToEdit.cnpj || "",
        grupo: filialToEdit.grupo || "",
        clienteId: filialToEdit.cliente?.id?.toString() || "",
      });
    }
  }, [open, filialToEdit, form]);

  const onSubmit = async (values: FilialFormValues) => {
    if (!filialToEdit) return;

    try {
      // Prepara o payload removendo campos vazios para não sobrescrever com null indesejado
      const payload = {
        nome: values.nome,
        cnpj: values.cnpj || undefined,
        grupo: values.grupo || undefined,
        clienteId: values.clienteId ? Number(values.clienteId) : undefined,
      };

      await updateFilial({ id: filialToEdit.id, payload });
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao atualizar filial:", error);
    }
  };

  return {
    form,
    isSubmitting: isUpdating,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
